import React, { useEffect, useState, useRef } from 'react';
import G6 from '@antv/g6';
import { IGraphData, IGraphNode, ILegendPoint } from './GraphHooks';
import { transformAntVData } from '../../helpers/functions/graphDataTransform';
import ExpandGraph from './ExpandGraph';
import GraphLegend from './GraphLegend';
import Loading from '../Loading';

interface Props {
  filteredData: Map<string, IGraphNode>;
  // Structural-only filter result (artist toggle ignored); seeds first-degree expansion.
  structuralFilteredData?: Map<string, IGraphNode>;
  // Artists individually toggled off; hidden after expansion without dropping neighbors.
  artistHiddenIds?: Set<string>;
  data: IGraphData;
  legend: ILegendPoint[];
  handleClick: (nodeId: string) => void;
  expandGraph: boolean;
  setExpandGraph: React.Dispatch<React.SetStateAction<boolean>>;
  gpuEnabled?: boolean;
  showFirstDegree?: boolean;
}

export const Graph: React.FC<Props> = ({
  data,
  filteredData,
  structuralFilteredData,
  artistHiddenIds,
  expandGraph,
  legend,
  gpuEnabled = false,
  showFirstDegree = false,
  setExpandGraph,
  handleClick
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // let graph: any = null;
  const [graph, setGraph] = useState(null);

  // tool tip
  const [ticking, setTicking] = useState(true);

  const clearNodes = () => {
    graph.getEdges()?.forEach(function(edge: any) {
      graph.clearItemStates(edge, ['active', 'inactive', 'hover']);
    });
    graph.getNodes()?.forEach(function(node: any) {
      if (node) {
        graph.setItemState(node, 'active', false);
        graph.setItemState(node, 'inactive', false);
      }
    });
  };

  const filterEvents = (filteredData: any) => {
    clearNodes();
    // For first-degree expansion, seed visibility from the structural filters only so an
    // artist toggled off still anchors its neighbors; otherwise seed from the full result.
    const seedFilteredData =
      showFirstDegree && structuralFilteredData ? structuralFilteredData : filteredData;
    const visibleIds = new Set<string>();
    graph.getNodes()?.forEach(function(node: any) {
      if (!seedFilteredData.has(node._cfg.id)) {
        visibleIds.add(node._cfg.id);
      }
    });
    if (showFirstDegree) {
      graph.getEdges()?.forEach(function(edge: any) {
        const { source, target } = edge._cfg.model;
        if (visibleIds.has(source)) {
          visibleIds.add(target);
        }
      });
      // Overlay individually toggled-off artists: hide just those nodes, keeping the
      // neighbors they pulled in. Their edges are dropped by the either-endpoint check below.
      artistHiddenIds?.forEach(id => visibleIds.delete(id));
    }
    graph.getNodes()?.forEach(function(node: any) {
      const visible = visibleIds.has(node._cfg.id);
      graph.setItemState(node, 'filtered', !visible);
      if (visible) {
        graph.showItem(node);
      } else {
        graph.hideItem(node);
      }
    });
    graph.getEdges()?.forEach(function(edge: any) {
      const { source, target } = edge._cfg.model;
      const hidden = !visibleIds.has(source) || !visibleIds.has(target);
      graph.setItemState(edge, 'filtered', hidden);
      if (hidden) {
        graph.hideItem(edge);
      } else {
        graph.showItem(edge);
      }
    });
  };

  const bindEvents = () => {
    if (!graph || !graph.destroyed) {
      if (ref || ref.current.offsetWidth || ref.current.offsetHeight) {
        const resizeObserver = new ResizeObserver(entries => {
          if (ref.current && !graph.destroyed) {
            graph.changeSize(ref.current.offsetWidth, ref.current.offsetHeight);
          }
        });
        resizeObserver.observe(ref.current);
      }
    }

    const highlightNeighbourNodes = (item: any) => {
      if (item.hasState('filtered')) return;
      clearNodes();
      graph.getNodes().forEach(function(node: any) {
        if (node.hasState('filtered')) return;
        graph.setItemState(node, 'inactive', true);
      });
      graph.setItemState(item, 'active', true); //activate current node
      graph.getNeighbors(item).forEach(function(node: any) {
        if (node.hasState('filtered')) return;
        node._cfg.edges.forEach(function(edge: any) {
          if (edge.hasState('filtered')) return;
          if (edge._cfg.model.source === item._cfg.model.id) {
            graph.setItemState(edge, 'active', true);
            graph.setItemState(node, 'active', true);
          }
        });
      });
    };

    // Gets rid of all events on edges so that they don't interfere with the canvas drag
    graph.getEdges().forEach((edge: any) => {
      const group = edge.getContainer();
      group.set('capture', false);
    });

    graph.on('afterlayout', (e: any) => {
      setTicking(false);
    });

    graph.on('node:click', (evt: any) => {
      const { item } = evt;
      const model = item.getModel();
      const { id } = model;
      handleClick(id);
    });

    graph.on('node:mouseenter', (evt: any) => {
      const { item, target } = evt;
      if (target.cfg.type === 'text') {
        return;
      }
      highlightNeighbourNodes(item);
    });

    graph.on('node:touchstart', (evt: any) => {
      // clearNodes();
      const { item } = evt;
      graph.setItemState(item, 'selected', true); // set node style
      highlightNeighbourNodes(item);
    });

    graph.on('canvas:touchstart', (evt: any) => {
      clearNodes();
    });

    graph.on('node:mouseout', (evt: any) => {
      clearNodes();
    });
  };

  useEffect(() => {
    if (graph) {
      graph.destroy();
    }
    if (data) {
      const temp = new G6.Graph({
        container: ref.current,
        width: ref.current.offsetWidth,
        height: ref.current.offsetHeight,
        fitView: true,
        modes: {
          default: [
            'drag-node',
            {
              type: 'drag-canvas',
              enableOptimize: true
              // allowDragOnItem: true,
            },
            {
              type: 'zoom-canvas',
              sensitivity: 1,
              enableOptimize: true
            }
          ]
        },
        defaultNode: {
          shape: 'node',

          labelPosition: 'right',
          // 节点文本样式
          labelCfg: {
            position: 'right',
            style: {
              fill: '#000',
              fontFamily: 'sans-serif',
              fontSize: 10
            }
          },
          // 节点默认样式
          style: {
            stroke: '#72CC4A',
            width: 150
          }
        },
        defaultEdge: {
          shape: 'line',
          style: {
            endArrow: true
          }
        },
        // 节点交互状态配置
        nodeStateStyles: {
          inactive: {
            opacity: 0.2
          },
          active: {
            opacity: 1
          },
          filtered: {
            opacity: 0,
            'text-shape': {
              opacity: 0
            }
          }
        },
        edgeStateStyles: {
          hover: {
            stroke: 'blue',
            lineWidth: 3
          },
          filtered: {
            opacity: 0
          }
        },
        layout: {
          type: 'gForce',
          preventOverlap: true,
          maxIteration: gpuEnabled ? 1000 : 100,
          edgeStrength: 500,
          gpuEnabled: gpuEnabled // Whether to enable the GPU parallel computing, supported by G6 4.0
        }
      });
      setGraph(temp);
    }
  }, [data]);

  useEffect(() => {
    if (graph && !graph.destroyed) {
      graph.data(transformAntVData(data));
      graph.render();
      bindEvents();
      filterEvents(filteredData);
    }
  }, [graph]);

  useEffect(() => {
    if (graph) {
      filterEvents(filteredData);
    }
  }, [filteredData]);

  return (
    <div className="relative w-full h-full shadow rounded-l bg-white">
      <div className={`absolute inset-0 ${ticking ? 'visible' : 'invisible'}`}>
        <Loading className="absolute left-4 top-4 w-8 h-8 border-2" />
      </div>
      <div className={`absolute inset-0 ${ticking ? 'invisible' : 'visible'}`} ref={ref}>
        <ExpandGraph expandGraph={expandGraph} setExpandGraph={setExpandGraph} />
        <GraphLegend legend={legend} />
      </div>
    </div>
  );
};
